import type {
  CategorySummary,
  ContentBlock,
  ProductListItem,
} from "@/lib/storefront-types";

// Kept dependency-free (type-only imports) so the module can be unit-tested
// under `node --experimental-strip-types --test`, like seo-organization.ts.

export type BreadcrumbItem = {
  name: string;
  path: string;
};

/**
 * Resolves a root-relative path against the canonical site origin. The host is
 * always forced to `origin` so a CMS-supplied absolute URL pointing at a
 * staging host can never leak into structured data (mirrors seo-url.ts).
 */
function absoluteUrl(origin: string, path: string): string {
  const trimmed = path.trim();
  if (!trimmed) {
    return origin;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return `${origin}${new URL(trimmed).pathname}`;
    } catch {
      return origin;
    }
  }
  return `${origin}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
}

/**
 * Media URLs keep their own host (Umbraco media may live on another domain);
 * only root-relative paths are prefixed with the site origin.
 */
function absoluteMediaUrl(origin: string, url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `${origin}${trimmed.startsWith("/") ? trimmed : `/${trimmed}`}`;
}

const HTML_ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

/** Flattens CMS rich text to plain text for schema description/answer fields. */
function htmlToPlainText(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&(?:amp|lt|gt|quot|#39|apos|nbsp);/g, (entity) => HTML_ENTITIES[entity] ?? " ")
    .replace(/\s+/g, " ")
    .replace(/ ([.,;:!?])/g, "$1")
    .trim();
}

function productDescription(product: ProductListItem): string {
  const text = product.summary || product.description || "";
  return htmlToPlainText(text);
}

/**
 * Root-relative CANONICAL product-detail-page path: the CMS-supplied
 * `canonicalPath` when present (cross-category duplicate), otherwise the
 * slug-built path — which mirrors `buildProductPath` in product-display.ts,
 * duplicated (5 lines) because this module must stay free of value imports to
 * run under `node --experimental-strip-types --test`. If you change the
 * slug-built fallback, change BOTH. (Navigation links deliberately keep using
 * `buildProductPath` so a shopper stays in the category they browsed.)
 */
function productPagePath(product: ProductListItem): string | null {
  // A cross-category duplicate carries the canonical PDP path from the CMS —
  // structured data always advertises the canonical page.
  const canonicalPath = product.canonicalPath?.trim();
  if (canonicalPath) {
    return canonicalPath;
  }

  const categorySlug = product.category.slug.trim();
  const productSlug = product.slug.trim();

  if (!categorySlug || !productSlug) {
    return null;
  }

  return `/menu/${categorySlug}/${productSlug}`;
}

function buildOffer(origin: string, product: ProductListItem) {
  return {
    "@type": "Offer",
    price: product.price.toFixed(2),
    priceCurrency: "GBP",
    availability: "https://schema.org/InStock",
    // Prefer the product's own detail page; fall back to the CMS-supplied URL
    // (the category listing) when a slug is missing.
    url: absoluteUrl(origin, productPagePath(product) ?? product.url ?? ""),
  };
}

/**
 * Full Product schema for a product detail page — the markup Google Merchant
 * listings (Shopping tab free listings) ingest. Emits name, description,
 * image, sku, brand and an Offer whose URL is the PDP itself.
 *
 * Deliberate omissions:
 * - `gtin`: own-brand prepared food has no GTIN; Google accepts brand-only
 *   identification for merchant-own products. Do not invent one.
 * - `shippingDetails`: frozen shipping is per-box (PricePerBox × boxCount,
 *   CMS-configured) and the food site is same-day local courier — neither is
 *   a single flat OfferShippingDetails rate. Merchant Center account-level
 *   shipping settings are the authoritative source instead.
 * - `priceValidUntil`: optional, and a rolling synthetic date is worse than
 *   the benign "missing field" notice in Search Console.
 */
export function buildProductSchema(
  origin: string,
  brandName: string,
  product: ProductListItem,
  productPath: string,
): Record<string, unknown> {
  const description = productDescription(product);
  const image = product.image?.url
    ? absoluteMediaUrl(origin, product.image.url)
    : null;
  const url = absoluteUrl(origin, productPath);
  const sku = product.sku?.trim();

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    ...(sku ? { sku } : {}),
    brand: {
      "@type": "Brand",
      name: brandName,
    },
    url,
    offers: {
      "@type": "Offer",
      price: product.price.toFixed(2),
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      url,
      // Perishable chef-cooked food: returns are not offered. Declaring the
      // policy (rather than omitting it) satisfies the merchant-listing
      // recommendation without inventing a returns flow.
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "GB",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      },
    },
  };
}

/** BreadcrumbList for any page; pass crumbs from the site root downwards. */
export function buildBreadcrumbList(
  origin: string,
  crumbs: BreadcrumbItem[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: absoluteUrl(origin, crumb.path),
    })),
  };
}

/**
 * ItemList of Product entries for a category listing page. Each Product (and
 * its Offer) points at the product's own detail page, falling back to the
 * category listing URL when a slug is missing.
 */
export function buildCategoryItemList(
  origin: string,
  category: CategorySummary,
  products: ProductListItem[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: category.name,
    url: absoluteUrl(origin, category.url),
    numberOfItems: products.length,
    itemListElement: products.map((product, index) => {
      const description = productDescription(product);
      const image = product.image?.url
        ? absoluteMediaUrl(origin, product.image.url)
        : null;

      return {
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Product",
          name: product.name,
          ...(description ? { description } : {}),
          ...(image ? { image } : {}),
          url: absoluteUrl(
            origin,
            productPagePath(product) ?? product.url ?? category.url,
          ),
          offers: buildOffer(origin, product),
        },
      };
    }),
  };
}

/**
 * Restaurant Menu schema for the menu hub page (Food site only — the Frozen
 * site is a shop, not a restaurant menu). Categories become MenuSections;
 * their items live on the category pages (buildMenuSectionSchema).
 */
export function buildMenuSchema(
  origin: string,
  brandName: string,
  categories: CategorySummary[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Menu",
    name: `${brandName} Menu`,
    url: absoluteUrl(origin, "/menu"),
    hasMenuSection: categories.map((category) => ({
      "@type": "MenuSection",
      name: category.name,
      ...(category.summary ? { description: category.summary } : {}),
      url: absoluteUrl(origin, category.url),
    })),
  };
}

/** MenuSection with MenuItems for a Food-site category page. */
export function buildMenuSectionSchema(
  origin: string,
  category: CategorySummary,
  products: ProductListItem[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "MenuSection",
    name: category.name,
    ...(category.summary ? { description: category.summary } : {}),
    url: absoluteUrl(origin, category.url),
    hasMenuItem: products.map((product) => {
      const description = productDescription(product);

      return {
        "@type": "MenuItem",
        name: product.name,
        ...(description ? { description } : {}),
        offers: buildOffer(origin, product),
      };
    }),
  };
}

/**
 * Plain ItemList of category links for the Frozen /menu hub. Menu/MenuSection
 * is restaurant vocabulary (Food site only — buildMenuSchema); the Frozen
 * site is a shop, so its menu hub lists the categories as a generic ItemList.
 */
export function buildCategoryLinkList(
  origin: string,
  name: string,
  categories: CategorySummary[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url: absoluteUrl(origin, "/menu"),
    numberOfItems: categories.length,
    itemListElement: categories.map((category, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: category.name,
      url: absoluteUrl(origin, category.url),
    })),
  };
}

/**
 * Titles that read as a question: end with "?" or start with an interrogative
 * lead word. Guards FAQPage against non-FAQ accordion content — Privacy/T&C
 * pages use numbered legal headings ("1. Who We Are") in accordions, and those
 * must not be emitted as FAQPage questions.
 */
const QUESTION_LEAD_WORD =
  /^(?:what|how|can|do|does|is|are|where|when|why|which|who|should)\b/i;

function titleReadsAsQuestion(title: string): boolean {
  return title.endsWith("?") || QUESTION_LEAD_WORD.test(title);
}

/**
 * FAQPage schema from a page's accordion blocks. Only accordion items whose
 * title reads as a question qualify, and the page needs at least two of them;
 * otherwise returns null so callers can skip the script entirely (no accordion
 * content, or legal pages whose accordions hold numbered headings).
 */
export function buildFaqSchema(
  blocks: ContentBlock[],
): Record<string, unknown> | null {
  const items = blocks
    .filter((block) => block.kind === "accordion")
    .flatMap((block) => block.accordionItems)
    .filter(
      (item) =>
        item.title.trim() &&
        titleReadsAsQuestion(item.title.trim()) &&
        htmlToPlainText(item.answerHtml),
    );

  if (items.length < 2) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.title.trim(),
      acceptedAnswer: {
        "@type": "Answer",
        text: htmlToPlainText(item.answerHtml),
      },
    })),
  };
}

/**
 * Service schema for a borough delivery landing page — tells search engines that
 * the brand provides Nigerian food delivery to this specific London borough
 * (the `areaServed`), strengthening local "Nigerian food delivery in <borough>"
 * relevance beyond the site-wide Restaurant schema.
 */
export function buildBoroughDeliveryServiceSchema(
  origin: string,
  brandName: string,
  boroughName: string,
  path: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Nigerian food delivery",
    name: `Nigerian Food Delivery in ${boroughName}`,
    url: absoluteUrl(origin, path),
    areaServed: {
      "@type": "AdministrativeArea",
      name: `${boroughName}, London`,
    },
    // Borough pages do not emit the full org node (buildStorefrontStructuredData),
    // so a bare { "@id" } reference would dangle on-page. A minimal typed node
    // keeps the reference resolvable, while the shared @id lets crawlers merge
    // it with the home page's full Restaurant node.
    provider: {
      "@type": "Restaurant",
      "@id": `${origin}/#organization`,
      name: brandName,
    },
  };
}
